import React, { useEffect, useState } from 'react';
import { CreditCard, DollarSign, Shield } from 'lucide-react';
import { teacherBillingService, type TeacherPaymentsOverview } from '@/services/teacherBilling';
import { useToast } from '@/components/ui/use-toast';
import { formatMoney } from '@/utils/datetime';
import {
  PromoCard,
  PromoCardBody,
  PromoCardDesc,
  PromoCardHeader,
  PromoCardTitle,
} from '@/components/promo/PromoCard';

const TeacherPaymentsPage: React.FC = () => {
  const { toast } = useToast();
  const [data, setData] = useState<TeacherPaymentsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [portalUrl, setPortalUrl] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setData(await teacherBillingService.getPaymentsOverview());
    } catch (err: unknown) {
      const detail =
        err &&
        typeof err === 'object' &&
        'response' in err &&
        err.response &&
        typeof err.response === 'object' &&
        'data' in err.response &&
        err.response.data &&
        typeof err.response.data === 'object' &&
        'detail' in err.response.data
          ? String((err.response.data as { detail?: string }).detail)
          : undefined;
      toast({
        title: 'Error',
        description: detail || 'Failed to load payments',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    void teacherBillingService
      .getPlatformCustomerLinks()
      .then((links) => setPortalUrl(links.customerPortalUrl ?? null))
      .catch(() => setPortalUrl(null));
  }, []);

  const upgrade = async (planSlug: string) => {
    if (planSlug === 'free') {
      toast({
        title: 'Free plan',
        description: 'You are on the free tier. Cancel PAYG in the billing portal to downgrade.',
      });
      return;
    }
    setCheckoutLoading(planSlug);
    try {
      const { checkoutUrl } = await teacherBillingService.startPlatformCheckout(
        planSlug,
        window.location.href
      );
      window.location.href = checkoutUrl;
    } catch {
      toast({ title: 'Error', description: 'Could not start checkout', variant: 'destructive' });
    } finally {
      setCheckoutLoading(null);
    }
  };

  const e = data?.earnings;
  const currency = e?.currencyCode || 'EUR';

  return (
    <PromoCard>
      <PromoCardHeader>
        <PromoCardTitle icon={<CreditCard size={20} strokeWidth={2} />}>
          Money &amp; payouts
        </PromoCardTitle>
        <PromoCardDesc>
          Your platform plan, student subscriptions, and estimated earnings after 15% fees.
        </PromoCardDesc>
      </PromoCardHeader>
      <PromoCardBody>
        {loading || !data ? (
          <p className="promo-empty">Loading…</p>
        ) : (
          <>
            <div className="money-grid">
              <div className="money-panel p-teal">
                <h4>
                  <DollarSign size={16} color="#0d9488" strokeWidth={2} />
                  Earnings (month to date)
                </h4>
                <div className="money-big">{formatMoney(e?.netEarningsCents ?? 0, currency)}</div>
                <div className="money-row">
                  <span>Gross student payments</span>
                  <b>{formatMoney(e?.grossCentsMonthToDate ?? 0, currency)}</b>
                </div>
                <div className="money-row">
                  <span>Platform fee (15%)</span>
                  <b>-{formatMoney(e?.commissionCents ?? 0, currency)}</b>
                </div>
                <div className="money-row">
                  <span>Active subscriptions</span>
                  <b>{e?.activeSubscriptions ?? 0}</b>
                </div>
                <div className="money-row">
                  <span>Pending lesson value</span>
                  <b>{formatMoney(e?.pendingLessonValueCents ?? 0, currency)}</b>
                </div>
              </div>
              <div className="money-panel p-violet">
                <h4>
                  <Shield size={16} color="#7c3aed" strokeWidth={2} />
                  Your platform plan
                </h4>
                <p style={{ fontSize: 12, color: '#64748b', lineHeight: 1.45, marginBottom: 8 }}>
                  Current: <strong>{data.currentPlatformPlan.name}</strong>
                  {data.currentPlatformPlan.status
                    ? ` (${data.currentPlatformPlan.status})`
                    : ''}
                </p>
                {portalUrl && (
                  <a
                    href={portalUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-violet-700 underline block mb-2"
                  >
                    Manage billing in Lemon (payment method, invoices)
                  </a>
                )}
                <div className="pricing-strip" style={{ marginTop: 0 }}>
                  {data.availablePlans.map((plan) => (
                    <div
                      key={plan.slug}
                      className={`price-card ${plan.isCurrent ? 'featured' : ''}`}
                    >
                      <h5>{plan.name}</h5>
                      <div className="ph">
                        {plan.billingType === 'pay_as_you_go'
                          ? 'Pay as you go'
                          : plan.monthlyAmountCents
                            ? formatMoney(plan.monthlyAmountCents, plan.currencyCode)
                            : 'Free'}
                      </div>
                      {plan.billingType === 'pay_as_you_go' && (
                        <span className="commission-pill">15% on student payments</span>
                      )}
                      {!plan.isCurrent && (
                        <button
                          type="button"
                          className="pkg-subscribe mt-2"
                          disabled={checkoutLoading === plan.slug}
                          onClick={() => void upgrade(plan.slug)}
                        >
                          {checkoutLoading === plan.slug ? 'Redirecting…' : 'Switch plan'}
                        </button>
                      )}
                      {plan.isCurrent && (
                        <p className="ps mt-2" style={{ color: '#047857', fontWeight: 600 }}>
                          Current plan
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="day-section">
              <div className="when">Student subscriptions &amp; payments</div>
              {data.studentPayments.length === 0 ? (
                <p className="promo-empty">No student subscriptions yet.</p>
              ) : (
                data.studentPayments.map((row) => (
                  <div key={row.subscriptionId} className="booking-tile" style={{ marginBottom: 8 }}>
                    <div>
                      <div className="t1">{row.studentName}</div>
                      <div className="t2">
                        {row.packageName || 'Lesson subscription'} ·{' '}
                        {formatMoney(row.priceCents, row.currencyCode)}
                        {row.cancelAtPeriodEnd ? ' · cancels at period end' : ''}
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`badge ${row.status === 'active' ? 'ok' : ''}`}>
                        {row.status.toUpperCase()}
                      </span>
                      <div className="text-xs text-slate-500 mt-1">
                        You keep {formatMoney(row.netAfterFeesCents, row.currencyCode)}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="proxy-banner">
              <div className="proxy-t">
                <Shield size={18} color="#4338ca" strokeWidth={2} />
                15% total · payments + payout
              </div>
              <p>
                Student charges run through Arrangelly&apos;s billing layer. Your estimated net
                above reflects a 15% platform fee on gross student payments this month.
              </p>
            </div>
          </>
        )}
      </PromoCardBody>
    </PromoCard>
  );
};

export default TeacherPaymentsPage;
